import { Box, Button, CircularProgress, Grid, MenuItem, Select, Typography } from '@mui/material'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import RefreshIcon from '@mui/icons-material/Refresh'
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent'
import { Page } from 'src/admin/core/components'
import { useAppTranslation } from 'src/admin/core/hooks'
import { ScreenDashboardProvider, useScreenDashboardContext } from './ScreenDashboardContext'
import SummaryCards from './components/SummaryCards'
import ScreenGroupCards from './components/ScreenGroupCards'
import ConsortiumHierarchy from './components/ConsortiumHierarchy'
import CriticalAlerts from './components/CriticalAlerts'

const ScreenDashboardInner = () => {
  const { label } = useAppTranslation()
  const {
    dashboardData,
    refreshData,
    autoRefreshInterval,
    setAutoRefreshInterval,
  } = useScreenDashboardContext()

  if (!dashboardData) {
    return (
      <Page>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 12,
            gap: 2,
          }}
        >
          <CircularProgress size={36} thickness={4} />
          <Typography variant="body2" color="text.secondary">
            Cargando dashboard...
          </Typography>
        </Box>
      </Page>
    )
  }

  const now = new Date()
  const formattedTime = now.toLocaleTimeString('es-DO', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })

  return (
    <Page
      pageTitle={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <MonitorHeartIcon color="primary" />
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 0, lineHeight: 1.2 }}>
              {label('SCREEN_DASHBOARD' as any)}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: '0.65rem' }}
            >
              {label('SCREEN_DASHBOARD_SUBTITULO' as any)}
            </Typography>
          </Box>
        </Box>
      }
      actions={
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
          {/* Consortium Filter */}
          {/* <Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.6rem',
                textTransform: 'uppercase',
                color: 'text.disabled',
                letterSpacing: 0.8,
                display: 'block',
                mb: 0.5,
              }}
            >
              {label('CONSORTIUM_FILTER' as any)}
            </Typography>
            <Select
              size="small"
              value={selectedConsortium}
              onChange={(e) => setSelectedConsortium(e.target.value)}
              sx={{ display: 'block', minWidth: 150, fontSize: '0.8rem' }}
            >
              <MenuItem value={dashboardData.consortium_name}>{dashboardData.consortium_name}</MenuItem>
            </Select>
          </Box> */}

          {/* Refresh Action */}
          <Box sx={{ display: 'flex' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => refreshData()}
              sx={{
                textTransform: 'none',
                minWidth: 'auto',
                px: 1,
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                borderRight: 0,
              }}
              title="Refrescar manualmente"
            >
              <RefreshIcon fontSize="small" />
            </Button>
            <Select
              size="small"
              value={autoRefreshInterval}
              onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
              sx={{
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                fontSize: '0.75rem',
                '& .MuiSelect-select': { py: 0.5, pl: 1, pr: 3 },
                '& fieldset': { borderColor: 'divider' },
                '&:hover fieldset': { borderColor: 'primary.main' },
              }}
              title="Intervalo de auto-refresco"
            >
              <MenuItem value={5} sx={{ fontSize: '0.8rem' }}>
                5 min
              </MenuItem>
              <MenuItem value={10} sx={{ fontSize: '0.8rem' }}>
                10 min
              </MenuItem>
              <MenuItem value={20} sx={{ fontSize: '0.8rem' }}>
                20 min
              </MenuItem>
              <MenuItem value={30} sx={{ fontSize: '0.8rem' }}>
                30 min
              </MenuItem>
            </Select>
          </Box>

          {/* Last Sync */}
          <Box sx={{ pb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.6rem',
                display: 'block',
                textTransform: 'uppercase',
                color: 'text.disabled',
                letterSpacing: 0.8,
                mb: 0.2,
              }}
            >
              {label('LAST_SYNC' as any)}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
              {dashboardData.last_sync}
            </Typography>
          </Box>

          {/* Manage Screens Button */}
          <Button
            variant="contained"
            startIcon={<SettingsInputComponentIcon />}
            href="/admin/process/screens"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {label('ADMINISTRAR_SCREENS' as any)}
          </Button>
        </Box>
      }
    >
      <Grid item xs={12}>
        <Box sx={{ px: 3, pb: 4 }}>
          {/* ── Summary Cards ── */}
          <SummaryCards data={dashboardData.summary} />

          {/* ── Screen Group Cards ── */}
          <ScreenGroupCards groups={dashboardData.screen_groups} />

          {/* ── Charts Row ── */}
          {/* <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={8}>
              <OverallHealthChart data={dashboardData.health_distribution} />
            </Grid>
          </Grid> */}

          {/* ── Consortium Hierarchy Accordion ── */}
          <Box sx={{ mb: 3 }}>
            <ConsortiumHierarchy
              agencies={dashboardData.agencies}
              agenciesCount={dashboardData.agencies_count}
              serversCount={dashboardData.servers_count}
            />
          </Box>

          {/* ── Bottom Row: Critical Alerts + System Health ── */}
          <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'stretch', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CriticalAlerts alerts={dashboardData.critical_alerts} />
            </Box>
            {/* <Box sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
              <SystemHealthPanel metrics={dashboardData.system_health} />
            </Box> */}
          </Box>
        </Box>
      </Grid>
    </Page>
  )
}

const ScreenDashboardPage = () => (
  <ScreenDashboardProvider>
    <ScreenDashboardInner />
  </ScreenDashboardProvider>
)

export default ScreenDashboardPage
